import { zodResolver } from "@hookform/resolvers/zod";
import { useAppDispatch, useAppSelector } from "hooks/hooks";
import React, { useEffect, useRef } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { XMarkIcon } from "@heroicons/react/24/solid";
import { validateClient } from "redux/features/auth/authSlice";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import Select from "shared/Select/Select";
import { z } from "zod";
import { motion, AnimatePresence } from "framer-motion";

const formSchema = z.object({
  validation: z.string().nonempty("Validation status is required"),
});

interface ValidateClientModalProps {
  client: { User?: { id: string }; lastName?: string; name?: string; validation?: string } | null;
  onClose: () => void;
  onValidate: () => void;
}

const ValidateClientModal: React.FC<ValidateClientModalProps> = ({ client, onClose, onValidate }) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    getValues,
  } = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      validation: client?.validation || "",
    },
    mode: "onSubmit",
  });

  const dispatch = useAppDispatch();
  const userState = useAppSelector((state) => state.auth);
  const modalRef = useRef<HTMLDivElement>(null);
  const isLoading = userState.loading === "pending";

  // Focus management for accessibility
  useEffect(() => {
    modalRef.current?.focus();
  }, []);

  // Handle form submission
  const onSubmit: SubmitHandler<z.infer<typeof formSchema>> = async (data) => {
    if (client?.User?.id) {
      await dispatch(validateClient({ data, id: client.User.id })).then(() => {
        onValidate();
      });
    }
  };

  // Handle Escape key to close modal
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      onClose();
    }
  };

  // Fallback for client name
  const displayName =
    client?.name || client?.lastName ? `${client.lastName || ""} ${client.name || ""}`.trim() : "Unknown Client";

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3, ease: "easeOut" }}
        className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70 backdrop-blur-sm"
        onKeyDown={handleKeyDown}
        role="dialog"
        aria-labelledby="modal-title"
        aria-modal="true"
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0, y: 50 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.9, opacity: 0, y: 50 }}
          transition={{ duration: 0.3, ease: "easeOut" }}
          ref={modalRef}
          tabIndex={-1}
          className="relative w-full max-w-lg p-8 mx-4 bg-white shadow-2xl dark:bg-neutral-800 rounded-3xl"
        >
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between pb-4 border-b border-gray-200 dark:border-neutral-700">
              <div className="space-y-1">
                <h2 id="modal-title" className="text-2xl font-bold tracking-tight text-gray-900 dark:text-white">
                  Validate Account
                </h2>
                <h3 className="text-lg font-medium text-gray-600 dark:text-gray-300">{displayName}</h3>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="p-2 text-gray-500 transition-colors rounded-full dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-neutral-700"
                aria-label="Close modal"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            {/* Form Content */}
            <div className="space-y-6">
              <div>
                <label
                  htmlFor="validation"
                  className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-200"
                >
                  Validation Status <span className="text-red-500">*</span>
                </label>
                <Select
                  id="validation"
                  {...register("validation")}
                  className={`w-full h-12 text-sm rounded-xl border ${
                    errors.validation
                      ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                      : "border-gray-300 dark:border-neutral-600 focus:ring-blue-500 focus:border-blue-500"
                  } bg-white dark:bg-neutral-900 text-gray-900 dark:text-gray-100 transition-all duration-200 appearance-none`}
                  onChange={(e) => setValue("validation", e.target.value, { shouldValidate: true })}
                  value={getValues("validation")}
                >
                  <option value="" disabled>
                    Select a status
                  </option>
                  <option value="en cours">In Progress</option>
                  <option value="approuvé">Approved</option>
                  <option value="annuler">Canceled</option>
                </Select>
                {errors.validation && (
                  <motion.p
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mt-2 text-sm text-red-600 dark:text-red-400"
                  >
                    {errors.validation.message}
                  </motion.p>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="flex justify-end pt-6 space-x-4 border-t border-gray-200 dark:border-neutral-700">
              <ButtonPrimary
                type="button"
                className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-gray-200 dark:bg-neutral-700 dark:text-gray-200 rounded-xl hover:bg-gray-300 dark:hover:bg-neutral-600 transition-all duration-200"
                onClick={onClose}
                disabled={isLoading}
              >
                Cancel
              </ButtonPrimary>
              <ButtonPrimary
                type="submit"
                className="flex items-center px-5 py-2.5 text-sm font-medium text-white bg-blue-600 rounded-xl hover:bg-blue-700 transition-all duration-200"
                loading={isLoading}
                disabled={isLoading}
              >
                {isLoading && (
                  <svg
                    className="w-4 h-4 mr-2 text-white animate-spin"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                )}
                Validate
              </ButtonPrimary>
            </div>
          </form>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default ValidateClientModal;
